/**
 * Client-side image compression using Canvas API.
 * Facebook / iPhone photos are 5-10MB and hit Cloudinary's free-tier 10MB limit.
 * Downscale images > 2MB to max 1920px longest side, JPEG q=85.
 * Non-images pass through untouched.
 */
export async function compressImageIfNeeded(file) {
  if (!file.type.startsWith("image/") || file.size < 2 * 1024 * 1024) return file;
  try {
    const bitmap = await createImageBitmap(file);
    const MAX = 1920;
    const scale = Math.min(1, MAX / Math.max(bitmap.width, bitmap.height));
    const w = Math.round(bitmap.width * scale);
    const h = Math.round(bitmap.height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    canvas.getContext("2d").drawImage(bitmap, 0, 0, w, h);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.85));
    if (!blob || blob.size >= file.size) return file;
    return new File([blob], file.name.replace(/\.(heic|heif|png|webp)$/i, ".jpg"), {
      type: "image/jpeg",
      lastModified: Date.now(),
    });
  } catch (e) {
    console.warn("Image compress failed, uploading original:", e);
    return file;
  }
}

/**
 * Upload a batch of files to Cloudinary using a signed request from our backend.
 * Returns array of { url, name, type } for uploaded files.
 * `onProgress(current, total)` is called as files finish.
 */
export async function uploadFiles(files, folder, API, axios, onProgress) {
  const { data: sig } = await axios.get(`${API}/cloudinary/signature`, {
    params: { resource_type: "image", folder },
  });
  const results = [];
  const arr = Array.from(files || []);
  let done = 0;
  for (const raw of arr) {
    const file = await compressImageIfNeeded(raw);
    const isImage = file.type.startsWith("image/");
    const resourceType = isImage ? "image" : "raw";
    const uploadUrl = `https://api.cloudinary.com/v1_1/${sig.cloud_name}/${resourceType}/upload`;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("api_key", sig.api_key);
    fd.append("timestamp", sig.timestamp);
    fd.append("signature", sig.signature);
    fd.append("folder", sig.folder);
    try {
      const { data } = await axios.post(uploadUrl, fd);
      results.push({ url: data.secure_url, name: file.name, type: isImage ? "image" : "raw" });
    } catch (e) {
      const cldErr = e?.response?.data?.error?.message;
      console.error("Upload failed", { file: file.name, cldErr, e });
      throw new Error(cldErr || `Upload failed: ${file.name}`);
    } finally {
      done++;
      onProgress && onProgress(done, arr.length);
    }
  }
  return results;
}
